import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, fetchOptions } from '../../config/api';

interface Appointment {
    id: string;
    timestamp: string;
    name: string;
    email: string;
    phone: string;
    service: string;
    date?: string;
    time?: string;
    message?: string;
}

interface Contact {
    id: string;
    timestamp: string;
    name: string;
    email: string;
    phone?: string;
    message: string;
}

interface Newsletter {
    id: string;
    timestamp: string;
    email: string;
}

interface Submissions {
    appointments: Appointment[];
    contacts: Contact[];
    newsletters: Newsletter[];
}

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState<'appointments' | 'contacts' | 'newsletters'>('appointments');
    const [submissions, setSubmissions] = useState<Submissions>({
        appointments: [],
        contacts: [],
        newsletters: []
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        checkAuth();
        fetchSubmissions();
    }, []);

    const checkAuth = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.admin.check, fetchOptions);
            const data = await response.json();
            if (!data.authenticated) {
                navigate('/admin/login');
            }
        } catch (error) {
            navigate('/admin/login');
        }
    };

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const response = await fetch(API_ENDPOINTS.admin.submissions, fetchOptions);
            if (response.ok) {
                const data = await response.json();
                setSubmissions(data);
            } else {
                navigate('/admin/login');
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch(API_ENDPOINTS.admin.logout, {
                method: 'POST',
                ...fetchOptions
            });
            navigate('/admin/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleDelete = async (type: string, id: string) => {
        if (!confirm('Are you sure you want to delete this submission?')) return;

        try {
            const response = await fetch(API_ENDPOINTS.admin.deleteSubmission(type, id), {
                method: 'DELETE',
                ...fetchOptions
            });

            if (response.ok) {
                fetchSubmissions(); // Refresh data
            }
        } catch (error) {
            console.error('Error deleting submission:', error);
        }
    };

    const formatDate = (timestamp: string) => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filterData = <T extends { name?: string; email: string; message?: string }>(data: T[]) => {
        if (!searchTerm) return data;
        return data.filter(item =>
            item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.message?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
            {/* Header */}
            <header className="bg-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                            <p className="text-gray-600 mt-1">Soft Meds by Erhu Medical Aesthetics</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/')}
                                className="text-amber-600 hover:text-amber-700 font-medium"
                            >
                                <i className="ri-home-line mr-2"></i>
                                Home
                            </button>
                            <button
                                onClick={handleLogout}
                                className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-6 py-2 rounded-lg hover:from-amber-700 hover:to-yellow-700 transition-all duration-300"
                            >
                                <i className="ri-logout-box-line mr-2"></i>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-600 text-sm font-medium">Appointments</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">{submissions.appointments.length}</p>
                            </div>
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-full flex items-center justify-center">
                                <i className="ri-calendar-check-line text-2xl text-amber-600"></i>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-600 text-sm font-medium">Contact Messages</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">{submissions.contacts.length}</p>
                            </div>
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-full flex items-center justify-center">
                                <i className="ri-mail-line text-2xl text-amber-600"></i>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-600 text-sm font-medium">Newsletter Subscribers</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">{submissions.newsletters.length}</p>
                            </div>
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-full flex items-center justify-center">
                                <i className="ri-notification-line text-2xl text-amber-600"></i>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="border-b border-gray-200">
                        <div className="flex space-x-8 px-6">
                            <button
                                onClick={() => setActiveTab('appointments')}
                                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'appointments'
                                    ? 'border-amber-600 text-amber-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <i className="ri-calendar-line mr-2"></i>
                                Appointments ({submissions.appointments.length})
                            </button>
                            <button
                                onClick={() => setActiveTab('contacts')}
                                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'contacts'
                                    ? 'border-amber-600 text-amber-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <i className="ri-message-line mr-2"></i>
                                Contact Messages ({submissions.contacts.length})
                            </button>
                            <button
                                onClick={() => setActiveTab('newsletters')}
                                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'newsletters'
                                    ? 'border-amber-600 text-amber-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <i className="ri-mail-send-line mr-2"></i>
                                Newsletter ({submissions.newsletters.length})
                            </button>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="relative">
                            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                            <input
                                type="text"
                                placeholder="Search by name, email, or message..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {loading ? (
                            <div className="text-center py-12">
                                <i className="ri-loader-4-line animate-spin text-4xl text-amber-600"></i>
                                <p className="text-gray-600 mt-4">Loading submissions...</p>
                            </div>
                        ) : (
                            <>
                                {/* Appointments Tab */}
                                {activeTab === 'appointments' && (
                                    <div className="overflow-x-auto">
                                        {filterData(submissions.appointments).length === 0 ? (
                                            <div className="text-center py-12">
                                                <i className="ri-calendar-line text-6xl text-gray-300"></i>
                                                <p className="text-gray-500 mt-4">No appointments found</p>
                                            </div>
                                        ) : (
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preferred Date/Time</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filterData(submissions.appointments).map((appointment) => (
                                                        <tr key={appointment.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {formatDate(appointment.timestamp)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {appointment.name}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                                <div>{appointment.email}</div>
                                                                <div className="text-gray-500">{appointment.phone}</div>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                                <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs">
                                                                    {appointment.service}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {appointment.date && appointment.time ? (
                                                                    <div>
                                                                        <div>{appointment.date}</div>
                                                                        <div className="text-gray-500">{appointment.time}</div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-gray-400">Not specified</span>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                                                {appointment.message || <span className="text-gray-400">No message</span>}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                <button
                                                                    onClick={() => handleDelete('appointments', appointment.id)}
                                                                    className="text-red-600 hover:text-red-800"
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}

                                {/* Contacts Tab */}
                                {activeTab === 'contacts' && (
                                    <div className="overflow-x-auto">
                                        {filterData(submissions.contacts).length === 0 ? (
                                            <div className="text-center py-12">
                                                <i className="ri-message-line text-6xl text-gray-300"></i>
                                                <p className="text-gray-500 mt-4">No contact messages found</p>
                                            </div>
                                        ) : (
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filterData(submissions.contacts).map((contact) => (
                                                        <tr key={contact.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {formatDate(contact.timestamp)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {contact.name}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                                <div>{contact.email}</div>
                                                                {contact.phone && <div className="text-gray-500">{contact.phone}</div>}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                                                                {contact.message}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                <button
                                                                    onClick={() => handleDelete('contacts', contact.id)}
                                                                    className="text-red-600 hover:text-red-800"
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}

                                {/* Newsletter Tab */}
                                {activeTab === 'newsletters' && (
                                    <div className="overflow-x-auto">
                                        {submissions.newsletters.length === 0 ? (
                                            <div className="text-center py-12">
                                                <i className="ri-mail-send-line text-6xl text-gray-300"></i>
                                                <p className="text-gray-500 mt-4">No newsletter subscribers found</p>
                                            </div>
                                        ) : (
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Date</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {submissions.newsletters
                                                        .filter(newsletter =>
                                                            !searchTerm || newsletter.email.toLowerCase().includes(searchTerm.toLowerCase())
                                                        )
                                                        .map((newsletter) => (
                                                            <tr key={newsletter.id} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    {formatDate(newsletter.timestamp)}
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                                    {newsletter.email}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                    <button
                                                                        onClick={() => handleDelete('newsletter', newsletter.id)}
                                                                        className="text-red-600 hover:text-red-800"
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
